import React, { useState, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, DownloadIcon, ZoomInIcon, ZoomOutIcon, PrinterIcon } from 'lucide-react';
import { useTheme } from '../providers/ThemeProvider';
import { Navbar } from '../Navbar';
import { Footer } from '../Footer';
export const InventionNo7Viewer: React.FC = () => {
  const navigate = useNavigate();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const [zoom, setZoom] = useState(100);
  // Detect mobile device
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  // PDF URL for Invention No. 7 BWV 778
  const pdfUrl = 'https://www.mutopiaproject.org/ftp/BachJS/BWV778/bach-invention-07/bach-invention-07-let.pdf';
  // Use mobile-friendly PDF parameters on mobile devices
  const pdfViewUrl = isMobile ? `${pdfUrl}#view=Fit&zoom=page-width` : `${pdfUrl}#view=FitH`;
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 10, 200));
  };
  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 10, 50));
  };
  const handleDownload = () => {
    // Download the actual PDF
    const link = document.createElement('a');
    link.href = pdfUrl;
    link.download = 'Bach - Invention No. 7 BWV 778.pdf';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  const handlePrint = () => {
    window.print();
  };
  return <div className={`min-h-screen ${isDark ? 'bg-gradient-to-r from-[#0F1626] via-[#1E3A8A] to-[#2C3E50]' : 'bg-gray-50'}`} data-id="element-5002">
      <Navbar data-id="element-5003" />

      {/* Wrapper with top padding to account for fixed Navbar */}
      <div className="pt-20" data-id="element-5004">
        {/* Hero Banner with Image */}
        <div className="relative w-full" style={{
        minHeight: '320px'
      }} data-id="element-5005">
          <img src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=400&h=300&fit=crop" alt="Invention No. 7" className="w-full h-64 md:h-80 object-cover" data-id="element-5006" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-black/30 to-black/70" data-id="element-5007" />
          <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8" data-id="element-5008">
            <div className="max-w-7xl mx-auto" data-id="element-5009">
              <button onClick={() => navigate(-1)} className="mb-4 flex items-center text-white/90 hover:text-white transition-colors" data-id="element-5010">
                <ArrowLeftIcon className="h-5 w-5 mr-2" data-id="element-5011" />
                Back to Library
              </button>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2" data-id="element-5012">
                Invention No. 7 in E minor
              </h1>
              <p className="text-lg md:text-xl text-white/90" data-id="element-5013">BWV 778</p>
              <p className="text-base md:text-lg text-white/80 mt-1" data-id="element-5014">
                Johann Sebastian Bach
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className={`sticky top-20 z-10 ${isDark ? 'bg-navy-800/95 backdrop-blur-sm border-b border-white/10' : 'bg-white/95 backdrop-blur-sm border-b border-gray-200'} shadow-sm`} data-id="element-5015">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between" data-id="element-5016">
          <div className="flex items-center space-x-2" data-id="element-5017">
            {!isMobile && <>
                <button onClick={handleZoomOut} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom out" data-id="element-5018">
                  <ZoomOutIcon className="h-5 w-5" data-id="element-5019" />
                </button>
                <span className={`text-sm font-medium px-3 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5020">
                  {zoom}%
                </span>
                <button onClick={handleZoomIn} className={`p-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white' : 'hover:bg-gray-100 text-gray-800'}`} aria-label="Zoom in" data-id="element-5021">
                  <ZoomInIcon className="h-5 w-5" data-id="element-5022" />
                </button>
              </>}
          </div>
          <div className="flex items-center space-x-2" data-id="element-5023">
            <button onClick={handlePrint} className={`flex items-center justify-center px-4 py-2 rounded-lg transition-colors ${isDark ? 'hover:bg-navy-700 text-white border border-navy-700' : 'hover:bg-gray-100 text-gray-800 border border-gray-200'}`} data-id="element-5024">
              <PrinterIcon className="h-4 w-4 sm:mr-2" data-id="element-5025" />
              <span className="hidden sm:inline" data-id="element-5026">Print</span>
            </button>
            <button onClick={handleDownload} className="flex items-center justify-center px-4 py-2 border-2 border-green-500 text-green-500 bg-transparent hover:bg-green-500/10 rounded-lg transition-colors" data-id="element-5027">
              <DownloadIcon className="h-4 w-4 sm:mr-2" data-id="element-5028" />
              <span className="hidden sm:inline" data-id="element-5029">Download PDF</span>
            </button>
          </div>
        </div>
      </div>

      {/* Sheet Music Viewer */}
      <main className="max-w-7xl mx-auto px-4 py-8" data-id="element-5030">
        <div className={`rounded-xl overflow-hidden shadow-lg ${isDark ? 'bg-navy-800' : 'bg-white'}`} data-id="element-5031">
          {/* PDF Viewer - Embedded */}
          <div className="w-full" style={{
          height: 'calc(100vh - 300px)',
          minHeight: '600px'
        }} data-id="element-5032">
            <iframe src={pdfViewUrl} className="w-full h-full" title="Invention No. 7 Sheet Music" style={{
            border: 'none',
            ...(isMobile ? {} : {
              transform: `scale(${zoom / 100})`,
              transformOrigin: 'top center',
              transition: 'transform 0.2s ease'
            })
          }} data-id="element-5033" />
          </div>
        </div>

        {/* Piece Information */}
        <div className={`mt-8 rounded-xl p-6 ${isDark ? 'bg-navy-800' : 'bg-white'} shadow-sm`} data-id="element-5034">
          <h2 className={`text-2xl font-bold mb-4 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5035">
            About This Piece
          </h2>
          <div className="grid md:grid-cols-2 gap-6" data-id="element-5036">
            <div data-id="element-5037">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5038">
                Composition Details
              </h3>
              <dl className="space-y-2" data-id="element-5039">
                <div data-id="element-5040">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5041">
                    Composer
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5042">
                    Johann Sebastian Bach
                  </dd>
                </div>
                <div data-id="element-5043">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5044">
                    Year Composed
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5045">
                    c. 1720-1723
                  </dd>
                </div>
                <div data-id="element-5046">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5047">
                    Form
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5048">
                    Two-Part Invention
                  </dd>
                </div>
                <div data-id="element-5049">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5050">
                    Key
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5051">
                    E minor
                  </dd>
                </div>
                <div data-id="element-5052">
                  <dt className={`text-sm font-medium ${isDark ? 'text-gray-400' : 'text-gray-600'}`} data-id="element-5053">
                    Catalog
                  </dt>
                  <dd className={`text-base ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5054">
                    BWV 778
                  </dd>
                </div>
              </dl>
            </div>
            <div data-id="element-5055">
              <h3 className={`text-lg font-semibold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`} data-id="element-5056">
                Performance Notes
              </h3>
              <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-5057">
                The seventh of Bach's fifteen Two-Part Inventions, this piece in
                E minor is notable for its energetic character and perpetual
                motion. Written as pedagogical works for keyboard students, the
                Inventions teach the art of two-voice counterpoint while
                developing musical ideas through systematic elaboration. This
                invention features a lively, running sixteenth-note subject that
                creates a sense of continuous forward momentum throughout the
                piece. The minor key lends the work a serious, focused quality,
                while the constant rhythmic activity demands precise finger work
                and evenness from the performer. The piece demonstrates Bach's
                mastery of invertible counterpoint, with the two voices
                frequently exchanging roles in a seamless dialogue. The
                technical challenges include maintaining clarity in the rapid
                passages, managing the hand crossings that occur when the voices
                intersect, and bringing out the contrapuntal lines while
                maintaining the overall sense of drive and direction. This
                invention serves as both an essential study in baroque keyboard
                technique and a compelling musical statement that showcases
                Bach's genius for creating sophisticated musical architecture
                within a compact, pedagogical framework.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-5058" />
    </div>;
};